// Synthetic session-replay dataset for preview mode. This is the standalone
// Session Replay dashboard's counterpart to the AI-chat session_replay cards
// (preview-mode/ai-chat/responses.ts): the same Meridian Health narrative,
// the same recording shape, and — for the recordings the AI surfaces — the
// same session ids, so a "View N more sessions" link from a chat card lands
// on a populated, filtered list and any card can be reopened from the
// standalone playback page.
//
// The dataset is deterministic (seeded PRNG) so list ordering, pagination,
// and filtering are stable across reloads and test runs. No backend or rrweb
// events are involved — the standalone playback page renders the same
// synthetic PreviewModePlayer the AI modal uses, driven directly off these
// records.
import type { SessionReplayItem } from "@/ai-chat/schema/assistantResponse";
import type {
  ListRecordingsParams,
  ListRecordingsResponse,
  SessionRecording,
} from "@/sessionReplay/api";

import { createRng } from "../graph/seed";

const PREVIEW_RECORDINGS_SEED = 0xce17_33;
const DEFAULT_LIMIT = 25;
const ORIGIN = "https://meridianhealth.example";

// Anchor every "X minutes ago" label to module-load time so the list feels
// freshly recorded on each app load. Matches the approach in
// ai-chat/responses.ts; acceptable drift for a preview session.
const MODULE_LOAD_MS = Date.now();
const minutesAgoIso = (mins: number): string =>
  new Date(MODULE_LOAD_MS - mins * 60_000).toISOString();

const UA = {
  iphone:
    "Mozilla/5.0 (iPhone; CPU iPhone OS 17_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.2 Mobile/15E148 Safari/604.1",
  android:
    "Mozilla/5.0 (Linux; Android 14; Pixel 8) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Mobile Safari/537.36",
  ipad: "Mozilla/5.0 (iPad; CPU OS 17_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.2 Mobile/15E148 Safari/604.1",
  chromeWin:
    "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Safari/537.36",
  chromeMac:
    "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Safari/537.36",
  safariMac:
    "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.2 Safari/605.1.15",
} as const;

const DEVICE_UA: Record<string, readonly string[]> = {
  mobile: [UA.iphone, UA.android],
  desktop: [UA.chromeWin, UA.chromeMac, UA.safariMac],
  tablet: [UA.ipad],
};

const u = (path: string): string => `${ORIGIN}${path}`;

// The recordings the AI "insurance verification drop-off" session_replay card
// surfaces. This module is the single source of truth: ai-chat/responses.ts
// imports these exact items into its canned block, and the standalone
// dashboard derives SessionRecordings from them below — so a card opened from
// AI and the same card opened from /session-replay are byte-for-byte the same
// recording (id, journey, device, source, duration).
//
// Durations are intentionally clamped to a 30–45s demo window so a viewer can
// watch a recording end-to-end; the AI's "analysis" copy describes the
// realistic, longer session each demo recording stands in for. A test in
// ai-chat/__tests__/responses.test.ts enforces that window.
export const PREVIEW_INSURANCE_DROP_ITEMS: readonly SessionReplayItem[] = [
  {
    sessionId: "sess_preview_insurance_drop_01",
    deviceId: "dev_preview_a1b2c3d4-e5f6-4789-aaaa-1111deadbeef",
    startedAt: minutesAgoIso(42),
    durationMs: 42_000,
    deviceType: "mobile",
    userAgent: UA.iphone,
    trafficSource: "google",
    pageUrls: [
      u("/services/cardiology"),
      u("/book"),
      u("/book/service"),
      u("/book/provider"),
      u("/book/insurance"),
    ],
  },
  {
    sessionId: "sess_preview_insurance_drop_02",
    deviceId: "dev_preview_b2c3d4e5-f6a7-4890-bbbb-2222cafebabe",
    startedAt: minutesAgoIso(2 * 60 + 11),
    durationMs: 38_000,
    deviceType: "desktop",
    userAgent: UA.chromeWin,
    trafficSource: "direct",
    pageUrls: [
      u("/locations/chicago"),
      u("/book"),
      u("/book/service"),
      u("/book/provider"),
      u("/book/insurance"),
    ],
  },
  {
    sessionId: "sess_preview_insurance_drop_03",
    deviceId: "dev_preview_c3d4e5f6-a7b8-4901-cccc-3333feedface",
    startedAt: minutesAgoIso(5 * 60 + 36),
    durationMs: 35_000,
    deviceType: "mobile",
    userAgent: UA.android,
    trafficSource: "facebook",
    pageUrls: [
      u("/services/orthopedics"),
      u("/book"),
      u("/book/service"),
      u("/book/provider"),
      u("/book/insurance"),
    ],
  },
];

// The recordings the AI "mobile users who abandoned the booking flow"
// session_replay card surfaces. Same single-source-of-truth contract as the
// insurance items above; every item is a mobile device (a responses.test.ts
// assertion depends on it).
export const PREVIEW_MOBILE_ABANDON_ITEMS: readonly SessionReplayItem[] = [
  {
    sessionId: "sess_preview_mobile_abandon_01",
    deviceId: "dev_preview_d4e5f6a7-b8c9-4012-dddd-4444babecafe",
    startedAt: minutesAgoIso(28),
    durationMs: 32_000,
    deviceType: "mobile",
    userAgent: UA.iphone,
    trafficSource: "google",
    pageUrls: [
      u("/"),
      u("/services/maternity"),
      u("/book"),
      u("/book/service"),
    ],
  },
  {
    sessionId: "sess_preview_mobile_abandon_02",
    deviceId: "dev_preview_e5f6a7b8-c9d0-4123-eeee-5555deadc0de",
    startedAt: minutesAgoIso(3 * 60 + 7),
    durationMs: 40_000,
    deviceType: "mobile",
    userAgent: UA.android,
    trafficSource: "facebook",
    pageUrls: [
      u("/locations/milwaukee"),
      u("/book"),
      u("/book/service"),
      u("/book/provider"),
      u("/book/insurance"),
    ],
  },
  {
    sessionId: "sess_preview_mobile_abandon_03",
    deviceId: "dev_preview_f6a7b8c9-d0e1-4234-ffff-66660ddba11",
    startedAt: minutesAgoIso(8 * 60 + 19),
    durationMs: 30_000,
    deviceType: "mobile",
    userAgent: UA.ipad,
    trafficSource: "(direct)",
    pageUrls: [u("/services/cardiology"), u("/book"), u("/book/service")],
  },
];

// Adapts a canned SessionReplayItem to the dashboard's SessionRecording shape.
// Inverse of recordingToReplayItem below; round-tripping an anchor through
// both is a no-op, which the recordings test asserts.
function replayItemToRecording(item: SessionReplayItem): SessionRecording {
  return {
    id: item.sessionId,
    sessionId: item.sessionId,
    deviceId: item.deviceId,
    startTime: item.startedAt,
    durationMs: item.durationMs,
    lastChunkAt: item.startedAt,
    chunkCount: 0,
    pageUrls: item.pageUrls,
    deviceType: item.deviceType ?? "",
    userAgent: item.userAgent ?? "",
    trafficSource: item.trafficSource ?? "",
  };
}

// The six recordings the AI session_replay cards reference, projected into the
// dashboard's SessionRecording shape so their deep links resolve identically.
const ANCHOR_RECORDINGS: SessionRecording[] = [
  ...PREVIEW_INSURANCE_DROP_ITEMS,
  ...PREVIEW_MOBILE_ABANDON_ITEMS,
].map(replayItemToRecording);

// Journey templates the generator samples from. Each describes a plausible
// Meridian Health visit; the generator layers on device, traffic source,
// recency, and duration to produce a varied-but-deterministic list.
type JourneyTemplate = {
  paths: string[];
  minSec: number;
  maxSec: number;
};

const JOURNEY_TEMPLATES: readonly JourneyTemplate[] = [
  // Booking conversions (reached confirmation).
  {
    paths: [
      "/",
      "/services/orthopedics",
      "/book",
      "/book/service",
      "/book/provider",
      "/book/insurance",
      "/book/confirm",
    ],
    minSec: 120,
    maxSec: 240,
  },
  {
    paths: [
      "/services/primary-care",
      "/book",
      "/book/service",
      "/book/provider",
      "/book/insurance",
      "/book/confirm",
    ],
    minSec: 90,
    maxSec: 200,
  },
  // Insurance-step drop-offs (the dominant abandonment pattern).
  {
    paths: [
      "/services/cardiology",
      "/book",
      "/book/service",
      "/book/provider",
      "/book/insurance",
    ],
    minSec: 70,
    maxSec: 150,
  },
  {
    paths: [
      "/locations/chicago",
      "/book",
      "/book/service",
      "/book/provider",
      "/book/insurance",
    ],
    minSec: 60,
    maxSec: 140,
  },
  // Earlier-step drop-offs.
  {
    paths: ["/", "/services/maternity", "/book", "/book/service"],
    minSec: 35,
    maxSec: 90,
  },
  {
    paths: ["/locations/milwaukee", "/book", "/book/service", "/book/provider"],
    minSec: 45,
    maxSec: 110,
  },
  // Browse-only sessions.
  {
    paths: ["/", "/services/cardiology", "/providers"],
    minSec: 25,
    maxSec: 70,
  },
  {
    paths: ["/", "/locations/chicago", "/locations/milwaukee"],
    minSec: 20,
    maxSec: 60,
  },
  { paths: ["/services/orthopedics"], minSec: 12, maxSec: 40 },
];

const TRAFFIC_SOURCES: readonly { value: string; weight: number }[] = [
  { value: "google", weight: 5 },
  { value: "direct", weight: 4 },
  { value: "facebook", weight: 2 },
  { value: "bing", weight: 1 },
  { value: "newsletter", weight: 1 },
  { value: "linkedin", weight: 1 },
];

const DEVICE_TYPES: readonly { value: string; weight: number }[] = [
  { value: "mobile", weight: 5 },
  { value: "desktop", weight: 4 },
  { value: "tablet", weight: 1 },
];

const GENERATED_COUNT = 30;

function buildRecording(input: {
  sessionId: string;
  deviceId: string;
  startTime: string;
  durationMs: number;
  deviceType: string;
  userAgent: string;
  trafficSource: string;
  paths: string[];
}): SessionRecording {
  return {
    id: input.sessionId,
    sessionId: input.sessionId,
    deviceId: input.deviceId,
    startTime: input.startTime,
    durationMs: input.durationMs,
    // Synthetic records have no chunk backing; the standalone player and the
    // AI modal both drive playback off the metadata, not these fields.
    lastChunkAt: input.startTime,
    chunkCount: 0,
    pageUrls: input.paths.map((p) => `${ORIGIN}${p}`),
    deviceType: input.deviceType,
    userAgent: input.userAgent,
    trafficSource: input.trafficSource,
  };
}

function generateRecordings(): SessionRecording[] {
  const rng = createRng(PREVIEW_RECORDINGS_SEED);
  const out: SessionRecording[] = [];

  for (let i = 0; i < GENERATED_COUNT; i++) {
    const template = rng.pick(JOURNEY_TEMPLATES);
    const deviceType = rng.weightedPick(DEVICE_TYPES);
    const userAgent = rng.pick(DEVICE_UA[deviceType] ?? [UA.chromeMac]);
    const trafficSource = rng.weightedPick(TRAFFIC_SOURCES);

    const spanSec = template.maxSec - template.minSec;
    const durationMs = (template.minSec + rng.int(spanSec + 1)) * 1000;

    // Spread sessions from ~12 minutes ago out to ~6 days ago, weighted
    // toward the recent end so the default "newest first" sort opens on
    // fresh-looking activity.
    const minutesAgo = 12 + rng.int(6 * 24 * 60);

    out.push(
      buildRecording({
        sessionId: `sess_preview_${rng.uuid()}`,
        deviceId: `dev_preview_${rng.uuid()}`,
        startTime: minutesAgoIso(minutesAgo),
        durationMs,
        deviceType,
        userAgent,
        trafficSource,
        paths: template.paths,
      }),
    );
  }

  return out;
}

// Built once per module load. Anchors lead so the default newest-first sort
// surfaces the AI-referenced recordings near the top of the list.
export const PREVIEW_SESSION_RECORDINGS: readonly SessionRecording[] = [
  ...ANCHOR_RECORDINGS,
  ...generateRecordings(),
];

function pathOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function matchesFilters(
  rec: SessionRecording,
  params: ListRecordingsParams,
): boolean {
  if (params.device_type && rec.deviceType !== params.device_type) {
    return false;
  }
  if (
    params.traffic_source &&
    rec.trafficSource.toLowerCase() !== params.traffic_source.toLowerCase()
  ) {
    return false;
  }
  if (params.page_url) {
    const needle = params.page_url.toLowerCase();
    const hit = rec.pageUrls.some((u) =>
      pathOf(u).toLowerCase().includes(needle),
    );
    if (!hit) return false;
  }
  if (params.start_after && rec.startTime) {
    if (Date.parse(rec.startTime) < Date.parse(params.start_after)) {
      return false;
    }
  }
  if (params.start_before && rec.startTime) {
    if (Date.parse(rec.startTime) > Date.parse(params.start_before)) {
      return false;
    }
  }
  if (params.search) {
    const needle = params.search.toLowerCase();
    const haystack = [rec.sessionId, rec.deviceId, ...rec.pageUrls.map(pathOf)]
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  return true;
}

function startMs(rec: SessionRecording): number {
  return rec.startTime ? Date.parse(rec.startTime) : 0;
}

function sortRecordings(
  recordings: SessionRecording[],
  sort: string | undefined,
): SessionRecording[] {
  const sorted = [...recordings];
  switch (sort) {
    case "oldest":
      sorted.sort((a, b) => startMs(a) - startMs(b));
      break;
    case "longest":
      sorted.sort((a, b) => b.durationMs - a.durationMs);
      break;
    case "shortest":
      sorted.sort((a, b) => a.durationMs - b.durationMs);
      break;
    case "newest":
    default:
      sorted.sort((a, b) => startMs(b) - startMs(a));
      break;
  }
  return sorted;
}

// Mirrors the real /v2/internal/session-replay/recordings contract closely
// enough that SessionReplayPage's filter/sort/pagination UI behaves the same
// against synthetic data. The cursor is an opaque numeric offset string.
export function listPreviewRecordings(
  params: ListRecordingsParams = {},
): ListRecordingsResponse {
  const filtered = PREVIEW_SESSION_RECORDINGS.filter((rec) =>
    matchesFilters(rec, params),
  );
  const sorted = sortRecordings(filtered, params.sort);

  const limit = params.limit && params.limit > 0 ? params.limit : DEFAULT_LIMIT;
  const offset = params.cursor ? Number.parseInt(params.cursor, 10) || 0 : 0;
  const page = sorted.slice(offset, offset + limit);
  const nextOffset = offset + limit;
  const nextCursor = nextOffset < sorted.length ? String(nextOffset) : null;

  return { recordings: page, nextCursor };
}

export function getPreviewRecording(
  sessionId: string,
): SessionRecording | undefined {
  return PREVIEW_SESSION_RECORDINGS.find((r) => r.sessionId === sessionId);
}

// Adapts a synthetic SessionRecording to the SessionReplayItem shape the
// PreviewModePlayer consumes, so the standalone playback page can reuse the
// exact synthetic player the AI modal renders.
export function recordingToReplayItem(
  rec: SessionRecording,
): SessionReplayItem {
  return {
    sessionId: rec.sessionId,
    deviceId: rec.deviceId,
    startedAt: rec.startTime ?? new Date(MODULE_LOAD_MS).toISOString(),
    durationMs: rec.durationMs,
    deviceType: rec.deviceType || null,
    userAgent: rec.userAgent || null,
    trafficSource: rec.trafficSource || null,
    pageUrls: rec.pageUrls,
  };
}
